import { AuditDashboardStats, AuditTask } from "../../types";

/** Costruisce stats per i grafici anche se l'endpoint dashboard fallisce. */
export function buildStatsFromTasks(tasks: AuditTask[]): AuditDashboardStats {
  const active = tasks.filter((t) => t.status === "active");
  const completed = tasks.filter((t) => t.status === "completed");
  const cancelled = tasks.filter((t) => t.status === "cancelled");
  const overdue = active.filter((t) => t.overdue);

  const byUser: Record<
    string,
    { username: string; active_tasks: number; total_target: number; total_completed: number }
  > = {};

  for (const t of tasks) {
    if (t.status === "cancelled") continue;
    const uname = t.assignee_username;
    if (!byUser[uname]) {
      byUser[uname] = { username: uname, active_tasks: 0, total_target: 0, total_completed: 0 };
    }
    if (t.status === "active") {
      byUser[uname].active_tasks += 1;
      byUser[uname].total_target += t.target_count;
      byUser[uname].total_completed += Math.min(t.completed_count, t.target_count);
    }
  }

  const user_progress = Object.values(byUser).map((u) => ({
    ...u,
    percent: u.total_target > 0 ? Math.round((u.total_completed / u.total_target) * 100) : 0,
  }));
  user_progress.sort((a, b) => b.percent - a.percent);

  const task_bars = active.map((t) => ({
    id: t.id,
    username: t.assignee_username,
    label: `${t.assignee_username} · ${t.completed_count}/${t.target_count}`,
    completed: t.completed_count,
    target: t.target_count,
    remaining: Math.max(0, t.target_count - t.completed_count),
    percent: t.percent ?? 0,
    overdue: t.overdue,
  }));

  const total_target_active = active.reduce((s, t) => s + t.target_count, 0);
  const total_completed_active = active.reduce(
    (s, t) => s + Math.min(t.completed_count, t.target_count),
    0
  );

  return {
    totals: {
      active: active.length,
      completed: completed.length,
      cancelled: cancelled.length,
      overdue: overdue.length,
      total_target_active,
      total_completed_active,
      overall_percent:
        total_target_active > 0
          ? Math.round((total_completed_active / total_target_active) * 100)
          : 0,
    },
    status_breakdown: {
      active: active.length,
      completed: completed.length,
      cancelled: cancelled.length,
      overdue: overdue.length,
    },
    user_progress,
    task_bars,
  };
}

export function mergeDashboardStats(
  apiStats: AuditDashboardStats | null,
  tasks: AuditTask[]
): AuditDashboardStats {
  const fromTasks = buildStatsFromTasks(tasks);
  if (!apiStats) return fromTasks;
  return {
    ...apiStats,
    task_bars: apiStats.task_bars?.length ? apiStats.task_bars : fromTasks.task_bars,
    user_progress: apiStats.user_progress?.length
      ? apiStats.user_progress
      : fromTasks.user_progress,
    totals: { ...fromTasks.totals, ...apiStats.totals },
    status_breakdown: apiStats.status_breakdown ?? fromTasks.status_breakdown,
  };
}
